import { Helmet } from 'react-helmet';

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl: string;
  ogImage?: string;
  ogType?: string;
}

const SEOHelmet = ({ 
  title, 
  description, 
  keywords = "cleaning services, house cleaning, professional cleaning, Connecticut, routine cleaners",
  canonicalUrl,
  ogImage = "/assets/hero-cleaning.jpg",
  ogType = "website"
}: SEOHelmetProps) => {
  const fullTitle = `${title} | Routine Cleaners`;
  const baseUrl = "https://routinecleaners.com";
  const fullCanonicalUrl = `${baseUrl}${canonicalUrl}`;
  const fullOgImage = `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="language" content="English" />
      <meta name="author" content="Routine Cleaners" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="Routine Cleaners" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullCanonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullOgImage} />
      
      {/* Additional SEO tags */}
      <meta name="geo.region" content="US-CT" />
      <meta name="geo.placename" content="Connecticut" />
      
      {/* Structured Data - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Routine Cleaners",
          "description": description,
          "url": baseUrl,
          "telephone": "(475) 251-7156",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CT",
            "addressCountry": "US"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "Hartford"
            },
            {
              "@type": "City", 
              "name": "New Haven"
            },
            {
              "@type": "City",
              "name": "Stamford"
            },
            {
              "@type": "City",
              "name": "Bridgeport"
            },
            {
              "@type": "State",
              "name": "Connecticut"
            }
          ],
          "serviceType": [
            "House Cleaning Services",
            "Deep Cleaning",
            "Regular Cleaning", 
            "Commercial Cleaning",
            "Airbnb Cleaning",
            "Move In/Out Cleaning"
          ],
          "priceRange": "$$",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "100+"
          },
          "openingHours": [
            "Mo-Fr 08:00-17:00",
            "Sa 09:00-16:00"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cleaning Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Deep Cleaning",
                  "description": "Comprehensive deep cleaning service"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "Regular Cleaning",
                  "description": "Weekly/bi-weekly maintenance cleaning"
                }
              }
            ]
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;